import React, { useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { DocumentNode } from '../../../types/documents';
import { documentDownloadService } from '../../../services/documentDownloadService';
import { PublicationExpiresAtField } from '../PublicationExpiresAtField';
import {
  parsePublicationExpiresAtInput,
  toPublicationExpiresAtLocalValue,
} from '../../../utils/publicationExpiresAtUtils';

const readOnlyModules = { toolbar: false };

const readOnlyQuillStyles = `
.publish-preview-quill .ql-toolbar.ql-snow{display:none}
.publish-preview-quill .ql-container.ql-snow{border:0;font-size:.875rem}
.publish-preview-quill .ql-editor{padding:1.5rem;min-height:200px;white-space:pre-wrap;word-break:break-word}
`;

interface PublishPreviewModalProps {
  open: boolean;
  document: DocumentNode;
  onClose: () => void;
  onConfirm: (publicationExpiresAt: string | null) => void;
  isPublishing: boolean;
  initialPublicationExpiresAt?: string | null;
}

export const PublishPreviewModal: React.FC<PublishPreviewModalProps> = ({
  open,
  document,
  onClose,
  onConfirm,
  isPublishing,
  initialPublicationExpiresAt = null,
}) => {
  const isEdoc = document.nodeType === 'edoc';
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(isEdoc);
  const [error, setError] = useState('');
  const [publicationExpiresAtInput, setPublicationExpiresAtInput] = useState('');
  const [expiresAtError, setExpiresAtError] = useState<string | null>(null);

  useEffect(() => {
    if (!open)
    {
      return;
    }

    setPublicationExpiresAtInput(toPublicationExpiresAtLocalValue(initialPublicationExpiresAt));
    setExpiresAtError(null);
  }, [open, initialPublicationExpiresAt]);

  useEffect(() => {
    if (!open || !isEdoc)
    {
      return;
    }

    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setError('');
      try {
        const blob = await documentDownloadService.downloadDocument(document.id);
        const text = await blob.text();
        if (!cancelled)
        {
          setContent(text);
        }
      } catch {
        if (!cancelled)
        {
          setError('Não foi possível carregar o conteúdo do documento.');
        }
      } finally {
        if (!cancelled)
        {
          setIsLoading(false);
        }
      }
    };

    void load();
    return () => { cancelled = true; };
  }, [open, document.id, isEdoc]);

  const handleConfirm = () => {
    const parsed = parsePublicationExpiresAtInput(publicationExpiresAtInput);
    if (!parsed.ok)
    {
      setExpiresAtError(parsed.message);
      return;
    }

    setExpiresAtError(null);
    onConfirm(parsed.iso);
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !isPublishing && onClose()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`flex flex-col rounded-lg bg-white shadow-xl ${isEdoc ? 'w-[90vw] max-w-4xl max-h-[90vh]' : 'w-full max-w-md'}`}>
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-lg">
                  <div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Confirmar Publicação
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500">
                      {isEdoc
                        ? <>Revise o conteúdo abaixo antes de publicar <strong>{document.title}</strong>.</>
                        : <>Deseja publicar o documento <strong>{document.title}</strong>?</>
                      }
                    </p>
                  </div>
                  <button
                    onClick={onClose}
                    disabled={isPublishing}
                    className="text-gray-400 hover:text-gray-600 text-2xl leading-none p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                  >
                    ×
                  </button>
                </div>

                {isEdoc && (
                  <div className="flex-1 overflow-auto p-6 bg-gray-50">
                    {isLoading && (
                      <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                      </div>
                    )}

                    {error && (
                      <div className="text-center py-20 text-red-600">{error}</div>
                    )}

                    {!isLoading && !error && (
                      <>
                        <style>{readOnlyQuillStyles}</style>
                        <div className="publish-preview-quill bg-white rounded-md border border-gray-200 shadow-sm">
                          <ReactQuill
                            value={content}
                            readOnly
                            theme="snow"
                            modules={readOnlyModules}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {!isEdoc && (
                  <div className="px-6 py-4 text-sm text-gray-600">
                    Após a publicação, o documento ficará disponível para consulta e poderá receber solicitações de assinatura.
                  </div>
                )}

                <div className="px-6 py-4 border-t bg-white">
                  <PublicationExpiresAtField
                    value={publicationExpiresAtInput}
                    onChange={(value) => {
                      setPublicationExpiresAtInput(value);
                      if (expiresAtError)
                      {
                        setExpiresAtError(null);
                      }
                    }}
                    disabled={isPublishing}
                  />
                  {expiresAtError && (
                    <p className="mt-2 text-sm text-red-600">{expiresAtError}</p>
                  )}
                </div>

                <div className="flex justify-end gap-3 px-6 py-4 border-t bg-white rounded-b-lg">
                  <button
                    type="button"
                    onClick={onClose}
                    disabled={isPublishing}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={isPublishing || (isEdoc && (isLoading || !!error))}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPublishing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Publicando...
                      </>
                    ) : (
                      'Confirmar Publicação'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
